import { DISTRICTS } from "../data/city";
import { GameState } from "../systems/GameState";
import { SaveManager } from "../core/SaveManager";
import { BRIEFINGS } from "../data/story";
import { Sfx } from "../systems/Sfx";
import { districtArt } from "./illustrations";
import { openProgressModal } from "./progressModal";
import { riskIcons, sectorStamp, inspectorRank, type SectorState } from "./prl";

/**
 * CENTRO DE OPERACIONES PRL (menú izquierdo). Panel de gestión de misiones del
 * inspector EPI: expediente de progreso + órdenes de intervención por sector,
 * con iconografía PRL, sellos de estado industrial y estética de caseta/obra.
 */

const HELMET = `<svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true"><path d="M4 16h16v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" fill="#f0c400"/><path d="M4 16c0-5 3-9 8-9s8 4 8 9" fill="none" stroke="#241d14" stroke-width="2"/><path d="M12 7V4" stroke="#241d14" stroke-width="2" stroke-linecap="round"/></svg>`;

const MISSION_ORDER = ["obras", "fabrica", "labo", "torre", "gris", "central"];
const EXTRA_ORDER = ["poligono", "bomberos", "hospital", "hotel"];

const EPI_ICON: Record<string, string> = {
  casco: `<svg viewBox="0 0 24 24" width="26" height="26"><path d="M4 16h16v2a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" fill="#f0a91b"/><path d="M4 16c0-5 3-9 8-9s8 4 8 9" fill="none" stroke="#c9830a" stroke-width="2"/><path d="M12 7V4" stroke="#c9830a" stroke-width="2" stroke-linecap="round"/></svg>`,
  gafas: `<svg viewBox="0 0 24 24" width="26" height="26" fill="#cfe6ec" stroke="#2c6270" stroke-width="1.7"><rect x="2.5" y="8.5" width="8" height="6.5" rx="3.2"/><rect x="13.5" y="8.5" width="8" height="6.5" rx="3.2"/><path d="M10.5 11.5h3M2.5 10l-1-2M21.5 10l1-2" fill="none" stroke-linecap="round"/></svg>`,
  auditiva: `<svg viewBox="0 0 24 24" width="26" height="26" fill="none"><path d="M4 13v-1a8 8 0 0 1 16 0v1" stroke="#241d14" stroke-width="1.8"/><rect x="2.5" y="12" width="5.5" height="8.5" rx="2" fill="#2f8f57" stroke="#1e6b3f" stroke-width="1.3"/><rect x="16" y="12" width="5.5" height="8.5" rx="2" fill="#2f8f57" stroke="#1e6b3f" stroke-width="1.3"/></svg>`,
  mascarilla: `<svg viewBox="0 0 24 24" width="26" height="26"><path d="M5 9h14v4a7 5 0 0 1-14 0z" fill="#eef2ee" stroke="#8a9296" stroke-width="1.4"/><path d="M5 10.5l-3-1.5M19 10.5l3-1.5" stroke="#8a9296" stroke-width="1.6" stroke-linecap="round"/><path d="M6.5 12h11M6.5 14.5h11" stroke="#c4cbce" stroke-width="1.4"/></svg>`,
  chaleco: `<svg viewBox="0 0 24 24" width="26" height="26"><path d="M7 4l5 3 5-3 3 3-2 2.5V20H6V9.5L4 7z" fill="#e9b70f" stroke="#b8890a" stroke-width="1.3"/><path d="M9 8.5V20M15 8.5V20" stroke="#dfe4e6" stroke-width="2"/><path d="M8.5 12.5h7M8.5 15.5h7" stroke="#dfe4e6" stroke-width="1.5"/></svg>`,
  guantes: `<svg viewBox="0 0 24 24" width="26" height="26" fill="#3a6ea0" stroke="#24455f" stroke-width="1.3"><path d="M8 21v-5l-2-2.2a1.4 1.4 0 0 1 2-2l1 1V6.2a1.4 1.4 0 0 1 2.7 0v4a1.4 1.4 0 0 1 2.7 0v1.2a1.4 1.4 0 0 1 2.5 1v2.4a5.5 5.5 0 0 1-5.5 5.2z"/></svg>`,
  botas: `<svg viewBox="0 0 24 24" width="26" height="26"><path d="M8 3h5v9l6 3v4H5v-5l3-2z" fill="#3a2c1e"/><rect x="5" y="18" width="14" height="3" rx="1" fill="#9aa0a8"/><path d="M8 12h5" stroke="#241d14" stroke-width="1.2"/></svg>`,
  arnes: `<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="#c9830a" stroke-width="2" stroke-linecap="round"><path d="M8.5 4v16M15.5 4v16M8.5 8l7 4.5M15.5 8l-7 4.5"/><rect x="6.5" y="10.5" width="11" height="3" rx="1" fill="#f0a91b" stroke="#c9830a" stroke-width="1"/></svg>`,
};

const EPIS: { key: string; name: string; protege: string }[] = [
  { key: "casco", name: "Casco", protege: "la cabeza de golpes y caídas de objetos" },
  { key: "gafas", name: "Gafas de seguridad", protege: "los ojos ante proyecciones y salpicaduras" },
  { key: "auditiva", name: "Protección auditiva", protege: "el oído frente al ruido que no avisa" },
  { key: "mascarilla", name: "Mascarilla", protege: "las vías respiratorias ante polvo y vapores" },
  { key: "chaleco", name: "Alta visibilidad", protege: "que te vean máquinas y vehículos" },
  { key: "guantes", name: "Guantes", protege: "las manos ante cortes, química y abrasión" },
  { key: "botas", name: "Botas de seguridad", protege: "los pies ante aplastamientos y pinchazos" },
  { key: "arnes", name: "Arnés anticaídas", protege: "en trabajos en altura, sujeto a un punto firme" },
];

const CHARACTERS: { name: string; color: string; text: string }[] = [
  { name: "EPI", color: "#f0c400", text: "Nuestro inspector. Mira lo que los demás prefieren no ver." },
  { name: "Marichei", color: "#df5f2a", text: "La guía de orientación de Mi Ruta. Da consejos para no perderte." },
  { name: "Manolo · Betis", color: "#0b7a3b", text: "Cuarenta años en la Fábrica 13. Entre piques de fútbol, cuenta lo que pasa de verdad en el tajo." },
  { name: "Paco · Sevilla", color: "#c4122e", text: "El otro veterano y amigo del alma de Manolo. Rivales en el fútbol, hermanos en lo demás." },
];

interface Status extends ReturnType<typeof sectorStamp> { state: SectorState; }

export class HubMenu {
  private root: HTMLElement;
  private missionsEl!: HTMLElement;
  private extraEl!: HTMLElement;
  private fileEl!: HTMLElement;
  private ayudaBtn?: HTMLButtonElement;
  private sig = "";
  private timer: number;

  constructor() {
    injectMenuStyles();
    this.root = document.getElementById("menu") ?? document.body;
    this.build();
    this.refresh();
    this.timer = window.setInterval(() => this.refresh(), 700);
  }

  private build(): void {
    this.root.innerHTML = "";

    const head = div("op-head");
    head.innerHTML =
      `<div class="op-head-top"><div class="op-logo">${HELMET}</div>` +
      `<div class="op-titles"><b>LAS AVENTURAS DE EPI</b><span>Centro de Operaciones PRL</span></div></div>` +
      `<div class="op-head-sub">Villa Segura · Unidad de Intervención</div>` +
      `<div class="op-tape"></div>`;
    this.root.appendChild(head);

    this.root.appendChild(plate("Expediente del inspector"));
    this.fileEl = div("op-file");
    this.root.appendChild(this.fileEl);

    this.root.appendChild(plate("Órdenes de intervención"));
    this.missionsEl = div("op-list");
    this.root.appendChild(this.missionsEl);

    this.root.appendChild(plate("Sectores extra"));
    this.extraEl = div("op-list");
    this.root.appendChild(this.extraEl);

    this.root.appendChild(plate("Operaciones"));
    const acts = div("op-acts");
    acts.append(
      this.actBtn("Manual de campo", () => this.showHowTo()),
      this.actBtn("Mochila de EPI", () => this.showKit()),
      this.actBtn("Dossier de personajes", () => this.showCharacters()),
    );
    this.root.appendChild(acts);

    this.root.appendChild(plate("Ajustes"));
    const cfg = div("op-acts");
    cfg.append(
      this.actBtn("Guardar / Continuar", () => openProgressModal()),
      this.actBtn("Probar sonido", () => { Sfx.unlock(); Sfx.resolve(); }),
      this.ayudaToggle(),
    );
    this.root.appendChild(cfg);

    const foot = div("op-foot");
    foot.innerHTML = `<span>No preguntes qué harías.<br><b>Hazlo.</b></span>`;
    this.root.appendChild(foot);
  }

  private actBtn(label: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.className = "op-act";
    b.textContent = label;
    b.onclick = () => { Sfx.click(); onClick(); b.blur(); };
    return b;
  }

  private ayudaToggle(): HTMLButtonElement {
    const b = document.createElement("button");
    b.className = "op-act op-toggle";
    this.ayudaBtn = b;
    this.syncAyuda();
    b.onclick = () => {
      const s = SaveManager.get();
      SaveManager.patch({ flags: { ...s.flags, ayuda: !s.flags?.ayuda } });
      Sfx.click(); this.syncAyuda(); b.blur();
    };
    return b;
  }
  private syncAyuda(): void {
    if (!this.ayudaBtn) return;
    const on = !!SaveManager.get().flags?.ayuda;
    this.ayudaBtn.innerHTML = `Modo ayuda <b>${on ? "ON" : "OFF"}</b>`;
    this.ayudaBtn.classList.toggle("on", on);
  }

  private status(id: string, st: GameState): Status {
    const def = DISTRICTS.find((d) => d.id === id)!;
    let state: SectorState;
    let sec: number | undefined;
    if (!def.sceneKey) state = "preparacion";
    else if (st.chapter(def.id).done) { state = "asegurada"; sec = st.security[def.id]; }
    else state = !def.requires || st.chapter(def.requires).done ? "disponible" : "restringido";
    return { state, ...sectorStamp(state, sec) };
  }

  refresh(): void {
    this.syncAyuda();
    const st = new GameState();
    const playable = DISTRICTS.filter((d) => d.sceneKey);
    const secured = playable.filter((d) => st.chapter(d.id).done).length;
    const risks = DISTRICTS.reduce((a, d) => a + st.chapter(d.id).hazards.length, 0);
    const points = playable.filter((d) => st.chapter(d.id).done).reduce((a, d) => a + (st.security[d.id] ?? 0), 0);

    const sig = playable.map((d) => d.id + this.status(d.id, st).label).join("|") + `#${secured}/${risks}/${points}`;
    if (sig === this.sig) return;
    this.sig = sig;

    // expediente del inspector
    const rk = inspectorRank(secured);
    const pct = Math.round((secured / rk.next) * 100);
    this.fileEl.innerHTML =
      `<div class="op-rank"><span class="op-rank-badge">${rk.rank}</span></div>` +
      `<div class="op-rank-bar"><i style="width:${Math.min(100, pct)}%"></i></div>` +
      `<div class="op-file-grid">` +
      fileStat("Sectores asegurados", `${secured}/${playable.length}`) +
      fileStat("Riesgos neutralizados", `${risks}`) +
      fileStat("Puntos PRL", `${points}`) +
      `</div>`;

    // órdenes
    this.missionsEl.innerHTML = "";
    for (const id of MISSION_ORDER) {
      const d = DISTRICTS.find((x) => x.id === id)!;
      this.missionsEl.appendChild(this.orderCard(d.id, d.name, d.sub, this.status(id, st)));
    }
    this.extraEl.innerHTML = "";
    for (const id of EXTRA_ORDER) {
      const d = DISTRICTS.find((x) => x.id === id)!;
      this.extraEl.appendChild(this.orderCard(d.id, d.name, d.sub, this.status(id, st)));
    }
  }

  private orderCard(id: string, name: string, sub: string, s: Status): HTMLElement {
    const card = document.createElement("button");
    card.className = "op-order " + s.cls;
    const lock = s.state === "restringido" ? `<span class="op-lock">${LOCK}</span>` : "";
    card.innerHTML =
      `<span class="op-order-strip"></span>` +
      `<span class="op-order-body">` +
      `<span class="op-order-head"><b>${name}</b>${lock}</span>` +
      `<span class="op-order-sub">${sub}</span>` +
      `<span class="op-risks">${riskIcons(id)}</span>` +
      `</span>` +
      `<span class="op-stamp ${s.cls}">${s.label}</span>`;
    card.onclick = () => { Sfx.click(); this.showBrief(id, name, sub, s); card.blur(); };
    return card;
  }

  // ---------- modales ----------
  private modal(title: string, headColor = "#241d14"): { body: HTMLElement; close: () => void } {
    const back = div("op-backdrop");
    const card = div("op-card");
    const head = div("op-card-head"); head.innerHTML = `<span class="op-card-tag"></span>${title}`; head.style.background = headColor;
    const body = div("op-card-body");
    const foot = div("op-card-foot");
    const ok = document.createElement("button"); ok.className = "op-close"; ok.textContent = "Cerrar";
    const close = () => back.remove();
    ok.onclick = () => { Sfx.click(); close(); };
    foot.appendChild(ok);
    card.append(head, body, foot); back.appendChild(card); document.body.appendChild(back);
    back.addEventListener("click", (e) => { if (e.target === back) close(); });
    return { body, close };
  }

  private showBrief(id: string, name: string, sub: string, s: Status): void {
    const brief = BRIEFINGS[id];
    const color = s.state === "asegurada" ? "#1e6b3f" : s.state === "disponible" ? "#8a5a10" : s.state === "restringido" ? "#7a1f16" : "#3a3630";
    const { body } = this.modal(name.toUpperCase(), color);
    const art = districtArt(id);
    body.innerHTML =
      (art ? `<div class="op-illus">${art}</div>` : "") +
      `<div class="op-brief-meta"><span class="op-stamp ${s.cls}">${s.label}</span><span class="op-sector">${sub}</span></div>` +
      `<div class="op-brief-risks">${riskIcons(id)}<span>Riesgos del sector</span></div>` +
      (brief
        ? `<p class="op-brief-lead">Parte de incidencias</p><p>${brief.body}</p>`
        : `<p>Sector en preparación. Pronto podrás intervenir aquí.</p>`) +
      (s.state === "restringido" ? `<p class="op-note">Acceso restringido: asegura antes el sector anterior.</p>` : "") +
      (s.state === "disponible" ? `<p class="op-note">Intervención disponible: acércate en la ciudad y pulsa <b>E</b> para entrar.</p>` : "");
  }

  private showHowTo(): void {
    const { body } = this.modal("MANUAL DE CAMPO", "#185fa5");
    body.innerHTML = `
      <ul class="op-ul">
        <li><b>Muévete</b> con WASD o las flechas. Entra en un sector con <b>E</b> cuando aparezca el aviso.</li>
        <li>En cada sitio: <b>observa</b>, <b>habla</b> con la gente (E) e <b>inspecciona</b> lo que no encaje.</li>
        <li>Ante un riesgo: <b>1)</b> identifícalo, <b>2)</b> elige la medida por la <b>jerarquía</b> (eliminar &gt; colectiva &gt; organizativa &gt; EPI) y <b>3)</b> aplícala paso a paso.</li>
        <li><b>Equípate</b> en la taquilla según el trabajo y el entorno: ni de más ni de menos.</li>
        <li>Herramientas: <b>Mochila (I)</b> y <b>Cuaderno (J)</b>. Silencia el sonido con <b>M</b>.</li>
        <li>La <b>Seguridad</b> sube cuando actúas bien. Asegura un sector para desbloquear el siguiente.</li>
        <li><b>Modo ayuda</b> (Ajustes o tecla <b>G</b>): activa una flecha que te guía. Desactivado por defecto, para que descubras tú.</li>
      </ul>
      <p class="op-lema">No preguntes qué harías. <b>Hazlo.</b></p>`;
  }

  private showKit(): void {
    const { body } = this.modal("MOCHILA DE EPI", "#8a5a10");
    const items = EPIS.map((e) => `<li><span class="op-kit-ico">${EPI_ICON[e.key]}</span><span><b>${e.name}</b> — protege ${e.protege}.</span></li>`).join("");
    body.innerHTML = `
      <p><b>EPI</b> = Equipo de Protección Individual. Es el <b>último recurso</b>, nunca el primero: cuando el peligro no se puede eliminar ni aislar, te protege a ti.</p>
      <ul class="op-kit">${items}</ul>`;
  }

  private showCharacters(): void {
    const { body } = this.modal("DOSSIER DE PERSONAJES", "#7a4b8a");
    const rows = CHARACTERS.map(
      (c) => `<li><span class="op-dot" style="background:${c.color}"></span><span><b>${c.name}</b> — ${c.text}</span></li>`,
    ).join("");
    body.innerHTML = `<ul class="op-chars">${rows}</ul><p class="op-note">…y las personas de cada sector, que saben más de lo que cuentan.</p>`;
  }

  destroy(): void { window.clearInterval(this.timer); }
}

const LOCK = `<svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><rect x="5" y="10" width="14" height="10" rx="1.5" fill="#c4122e"/><path d="M8 10V7a4 4 0 0 1 8 0v3" fill="none" stroke="#c4122e" stroke-width="2"/><circle cx="12" cy="15" r="1.6" fill="#241d14"/></svg>`;

function div(cls: string): HTMLElement { const n = document.createElement("div"); n.className = cls; return n; }
function plate(text: string): HTMLElement { const n = div("op-plate"); n.textContent = text; return n; }
function fileStat(label: string, value: string): string { return `<div class="op-fstat"><span>${label}</span><b>${value}</b></div>`; }

function injectMenuStyles(): void {
  if (document.getElementById("op-menu-styles")) return;
  const tape = "repeating-linear-gradient(45deg,#f0c400 0 11px,#171308 11px 22px)";
  const css = `
  #menu{font-family:"Segoe UI",system-ui,sans-serif;color:#e7dcc4;padding:0 0 20px;box-sizing:border-box;display:flex;flex-direction:column;gap:10px;background:#14120c;background-image:linear-gradient(#14120c,#14120c),radial-gradient(rgba(255,255,255,.02) 1px,transparent 1px);background-size:auto,6px 6px}
  #menu::-webkit-scrollbar{width:9px}#menu::-webkit-scrollbar-thumb{background:#3a2f1c;border-radius:0}
  /* cabecera: placa de control */
  .op-head{background:linear-gradient(#22201a,#1a1710);border-bottom:1px solid #3a2f1c;padding:12px 12px 0}
  .op-head-top{display:flex;align-items:center;gap:10px}
  .op-logo{width:42px;height:42px;flex:0 0 auto;background:#14120c;border:2px solid #3a2f1c;display:flex;align-items:center;justify-content:center;box-shadow:inset 0 0 0 1px #000}
  .op-titles{display:flex;flex-direction:column;line-height:1.12}
  .op-titles b{font-size:13.5px;color:#f4ead0;letter-spacing:.06em;font-weight:800}
  .op-titles span{font-size:10.5px;color:#f0c400;text-transform:uppercase;letter-spacing:.09em;font-weight:700;margin-top:2px}
  .op-head-sub{font-size:10px;color:#9c8b6c;text-transform:uppercase;letter-spacing:.08em;margin:7px 0 9px}
  .op-tape{height:8px;background:${tape};opacity:.85;margin:0 -12px}
  /* placas de sección (rótulos estarcidos) */
  .op-plate{margin:6px 12px 0;font-size:10.5px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#171308;background:#f0c400;padding:3px 8px;box-shadow:2px 2px 0 #000;align-self:flex-start;max-width:calc(100% - 24px)}
  /* expediente del inspector */
  .op-file{margin:0 12px;background:#1f1b12;border:1px solid #3a2f1c;border-left:4px solid #f0c400;padding:11px}
  .op-rank{display:flex;justify-content:space-between;align-items:center}
  .op-rank-badge{font-size:13px;font-weight:800;color:#f0c400;letter-spacing:.02em}
  .op-rank-bar{height:7px;background:#0d0a06;border:1px solid #3a2f1c;margin:7px 0 9px;overflow:hidden}
  .op-rank-bar>i{display:block;height:100%;background:${tape};transition:width .5s}
  .op-file-grid{display:grid;grid-template-columns:1fr 1fr 1fr;gap:6px}
  .op-fstat{background:#14120c;border:1px solid #2c2114;padding:6px 5px;text-align:center}
  .op-fstat span{display:block;font-size:8.5px;color:#9c8b6c;text-transform:uppercase;letter-spacing:.03em;line-height:1.2;min-height:2.2em}
  .op-fstat b{font-size:15px;color:#f4ead0;font-variant-numeric:tabular-nums}
  /* órdenes de intervención */
  .op-list{display:flex;flex-direction:column;gap:7px;padding:0 12px}
  .op-order{position:relative;display:flex;align-items:stretch;gap:0;text-align:left;border:1px solid #3a2f1c;background:linear-gradient(#211c12,#1a1610);padding:0;cursor:pointer;font-family:inherit;overflow:hidden;transition:transform .08s,box-shadow .1s}
  .op-order:hover{transform:translateX(2px);box-shadow:-3px 0 0 #f0c400}
  .op-order-strip{flex:0 0 7px;background:${tape}}
  .op-order.op-asegurada .op-order-strip{background:repeating-linear-gradient(45deg,#2f8f57 0 11px,#171308 11px 22px)}
  .op-order.op-restringido .op-order-strip{background:repeating-linear-gradient(45deg,#c4122e 0 11px,#171308 11px 22px)}
  .op-order.op-preparacion .op-order-strip{background:repeating-linear-gradient(45deg,#5a5348 0 11px,#171308 11px 22px)}
  .op-order-body{flex:1;min-width:0;padding:8px 8px 8px 10px}
  .op-order-head{display:flex;align-items:center;gap:5px}
  .op-order-head b{font-size:12.5px;color:#f4ead0;line-height:1.15}
  .op-order.op-preparacion .op-order-head b{color:#a89a80}
  .op-order-sub{display:block;font-size:9.5px;color:#9c8b6c;text-transform:uppercase;letter-spacing:.04em;margin-top:1px}
  .op-lock{display:inline-flex}
  .op-risks{display:flex;gap:3px;margin-top:6px;flex-wrap:wrap}
  .op-risk{width:20px;height:20px;background:#14120c;border:1px solid #2c2114;display:flex;align-items:center;justify-content:center}
  /* sellos de estado */
  .op-stamp{align-self:flex-start;margin:8px 8px 0 0;font-size:8px;font-weight:800;letter-spacing:.04em;padding:3px 6px;border:2px solid;transform:rotate(-3deg);white-space:nowrap;text-transform:uppercase;background:#14120c}
  .op-stamp.op-asegurada{color:#7fe0a6;border-color:#2f8f57}
  .op-stamp.op-disponible{color:#ffd489;border-color:#f0a91b}
  .op-stamp.op-restringido{color:#f0a08f;border-color:#c4122e}
  .op-stamp.op-preparacion{color:#9c8b6c;border-color:#5a5348}
  /* operaciones / ajustes */
  .op-acts{display:flex;flex-direction:column;gap:6px;padding:0 12px}
  .op-act{text-align:left;border:1px solid #3a2f1c;background:#1c1810;color:#e7dcc4;padding:9px 11px;font-weight:700;font-size:12px;letter-spacing:.02em;cursor:pointer;font-family:inherit;transition:background .12s,transform .08s;text-transform:uppercase}
  .op-act:hover{background:#262115;transform:translateX(2px)}
  .op-toggle{display:flex;justify-content:space-between;align-items:center}
  .op-toggle b{font-weight:800;color:#9c8b6c}
  .op-act.on{border-color:#2f8f57;background:#16281d;color:#cdeede}.op-act.on b{color:#7fe0a6}
  .op-foot{margin:6px 12px 0;padding-top:11px;border-top:1px dashed #3a2f1c;color:#9c8b6c;font-size:12px;line-height:1.45}
  .op-foot b{color:#f0c400}
  /* modales del centro */
  .op-backdrop{position:fixed;inset:0;background:rgba(10,8,4,.66);display:flex;align-items:center;justify-content:center;z-index:80;padding:16px;font-family:"Segoe UI",system-ui,sans-serif}
  .op-card{background:#efe3cb;border:2px solid #241d14;max-width:520px;width:100%;max-height:88vh;display:flex;flex-direction:column;overflow:hidden;box-shadow:0 20px 54px rgba(0,0,0,.6)}
  .op-card-head{padding:.7rem 1rem;color:#fff;font-weight:800;font-size:1rem;letter-spacing:.05em;display:flex;align-items:center;gap:.5rem}
  .op-card-tag{width:14px;height:14px;background:${tape};flex:0 0 auto;box-shadow:0 0 0 1px rgba(0,0,0,.3)}
  .op-card-body{padding:1rem;overflow:auto;color:#241d14;line-height:1.55;font-size:.98rem}
  .op-card-body p{margin:.2rem 0 .6rem}
  .op-illus{margin:-1rem -1rem 1rem;border-bottom:2px solid #241d14;overflow:hidden;line-height:0}
  .op-card-foot{padding:.7rem 1rem;background:#e6d7ba;display:flex;justify-content:flex-end;border-top:1px solid #cbb891}
  .op-close{padding:.5em 1.2em;border:2px solid #241d14;background:#f0c400;color:#241d14;font-weight:800;cursor:pointer;font-family:inherit;text-transform:uppercase;letter-spacing:.03em}
  .op-brief-meta{display:flex;align-items:center;gap:.6rem;margin-bottom:.5rem}
  .op-brief-meta .op-stamp{margin:0}
  .op-sector{font-size:.8rem;color:#6a5c44;text-transform:uppercase;letter-spacing:.05em}
  .op-brief-risks{display:flex;align-items:center;gap:.35rem;margin-bottom:.7rem;font-size:.75rem;color:#6a5c44;text-transform:uppercase;letter-spacing:.04em}
  .op-brief-risks .op-risk{background:#e6d7ba;border-color:#cbb891}
  .op-brief-lead{font-size:.72rem;font-weight:800;text-transform:uppercase;letter-spacing:.08em;color:#8a2018;margin-bottom:.15rem}
  .op-note{font-size:.85rem;color:#6a5c44;background:#e6d7ba;border-left:3px solid #f0a91b;padding:.5rem .65rem;margin-top:.5rem}
  .op-ul{margin:.2rem 0;padding-left:1.1rem;display:flex;flex-direction:column;gap:.45rem}
  .op-lema{margin-top:.7rem;font-style:italic;color:#8a2018;font-weight:600}.op-lema b{font-style:normal}
  .op-kit{padding-left:0;margin:.2rem 0;list-style:none;display:flex;flex-direction:column;gap:.5rem}
  .op-kit li{display:flex;gap:.6rem;align-items:flex-start;line-height:1.42}
  .op-kit-ico{flex:0 0 auto;width:34px;height:34px;background:#e6d7ba;border:1px solid #241d14;display:flex;align-items:center;justify-content:center}
  .op-chars{list-style:none;margin:.2rem 0;padding:0;display:flex;flex-direction:column;gap:.7rem}
  .op-chars li{display:flex;gap:.6rem;align-items:flex-start;line-height:1.42}
  .op-dot{flex:0 0 auto;width:14px;height:14px;margin-top:.2rem;box-shadow:0 0 0 1px rgba(0,0,0,.2)}
  @media(max-width:900px){#menu{display:none}}
  `;
  const style = document.createElement("style");
  style.id = "op-menu-styles";
  style.textContent = css;
  document.head.appendChild(style);
}
